/**
 * MattersHistory — widok „Moja Historia" dla anonimowych zgłoszeń z ekranu logowania.
 *
 * Czyta IDki zgłoszeń (matters + inquiries) z localStorage `mr_jerry_my_submissions`
 * i odpytuje backend po by-ids, prezentując status:
 *   - „odpowiedziane / wykonane" (zielone)
 *   - „oczekuje" (pomarańczowe)
 *
 * Token historii zapisywany jest w MattersFab.jsx przy zatwierdzeniu zgłoszenia.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import PortalOverlay from './common/PortalOverlay';

const API = process.env.REACT_APP_BACKEND_URL;
const HISTORY_KEY = 'mr_jerry_my_submissions';

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const LocBadge = ({ loc }) => {
  const map = { krakow: '🏰 Kraków', nowysacz: '🏔️ Nowy Sącz', grodek: '🏞️ Gródek' };
  return (
    <span style={{
      fontSize: 10, padding: '2px 6px', borderRadius: 999,
      background: 'rgba(212,175,55,0.18)', color: '#d4af37',
      border: '1px solid rgba(212,175,55,0.40)', fontWeight: 700,
    }}>{map[loc] || loc}</span>
  );
};

const StatusBadge = ({ kind, status, answered }) => {
  // matters: open / in_progress / done
  // inquiry: answered = true/false
  let color = '#f59e0b';
  let label = 'Oczekuje';
  if (kind === 'matter') {
    if (status === 'done') { color = '#22c55e'; label = 'Wykonane'; }
    else if (status === 'rejected') { color = '#ef4444'; label = 'Odrzucone'; }
    else if (status === 'in_progress') { color = '#3b82f6'; label = 'W trakcie'; }
    else { color = '#f59e0b'; label = 'Niewykonane'; }
  } else {
    if (answered) { color = '#22c55e'; label = 'Odpowiedziane'; }
    else { color = '#f59e0b'; label = 'Bez odpowiedzi'; }
  }
  return (
    <span style={{
      fontSize: 10, padding: '3px 8px', borderRadius: 999,
      background: `${color}26`, color, border: `1px solid ${color}80`, fontWeight: 800,
      letterSpacing: '0.03em', whiteSpace: 'nowrap',
    }}>{label}</span>
  );
};

const MattersHistory = ({ location }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({}); // { [groupId]: true }
  const [statusFilter, setStatusFilter] = useState('all'); // all|inquiries|commented|done|rejected|pending
  const [selected, setSelected] = useState(null); // pełny widok jednego itemu
  const toggleGroup = (id) => setCollapsed((s) => ({ ...s, [id]: !s[id] }));
  const loadIdRef = React.useRef(0);

  const load = React.useCallback(async (silent = false) => {
    const myLoadId = ++loadIdRef.current;
    if (silent) setRefreshing(true);
    else setLoading(true);
    let local = [];
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      local = raw ? JSON.parse(raw) : [];
    } catch { local = []; }

    const matterIds = local.filter((e) => e.type === 'matter').map((e) => e.id);
    const inquiryIds = local.filter((e) => e.type === 'inquiry').map((e) => e.id);

    try {
      const [myMatters, myInquiries, feedMatters, feedInquiries] = await Promise.all([
        matterIds.length
          ? axios.post(`${API}/api/restaurant-matters/matters/by-ids`, { ids: matterIds })
          : Promise.resolve({ data: { items: [] } }),
        inquiryIds.length
          ? axios.post(`${API}/api/restaurant-inquiries/by-ids`, { ids: inquiryIds })
          : Promise.resolve({ data: { items: [] } }),
        location
          ? axios.get(`${API}/api/restaurant-matters/matters`, { params: { location, lean: true } })
          : Promise.resolve({ data: { items: [] } }),
        location
          ? axios.get(`${API}/api/restaurant-inquiries/asked-feed`, { params: { location, limit: 50 } })
          : Promise.resolve({ data: { items: [] } }),
      ]);

      // Guard: jeśli w międzyczasie wystartował nowy load, zignoruj wynik (race condition)
      if (myLoadId !== loadIdRef.current) return;

      const mMap = Object.fromEntries((myMatters.data.items || []).map((m) => [m.id, m]));
      const iMap = Object.fromEntries((myInquiries.data.items || []).map((i) => [i.id, i]));

      const mine = local.map((e) => ({
        type: e.type,
        source: 'mine',
        localEntry: e,
        doc: e.type === 'matter' ? mMap[e.id] : iMap[e.id],
      })).filter((x) => x.doc);

      const submittedIds = new Set(local.map((e) => `${e.type}:${e.id}`));
      const feed = [
        ...(feedMatters.data.items || [])
          .filter((m) => !submittedIds.has(`matter:${m.id}`))
          .map((m) => ({ type: 'matter', source: 'feed', doc: m })),
        ...(feedInquiries.data.items || [])
          .filter((i) => !submittedIds.has(`inquiry:${i.id}`))
          .map((i) => ({ type: 'inquiry', source: 'feed', doc: i })),
      ];

      const all = [...mine, ...feed].sort((a, b) => {
        const ta = a.doc.completedAt || a.doc.answeredAt || a.doc.createdAt || '';
        const tb = b.doc.completedAt || b.doc.answeredAt || b.doc.createdAt || '';
        return tb.localeCompare(ta);
      });
      setItems(all);
    } catch (e) {
      if (myLoadId === loadIdRef.current) {
        toast.error('Błąd ładowania historii: ' + (e?.response?.data?.detail || e.message));
      }
    } finally {
      if (myLoadId === loadIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [location]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh przy powrocie do tab'a (visibility) — łapie zmiany statusu od managera
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') load(true); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  // Filtr tekstowy — zwraca true jeśli item pasuje do query (lub query pusty)
  const matchesSearch = (it) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const d = it.doc;
    const haystack = [
      d.title, d.description, d.question, d.answer, d.managerComment,
      d.createdByName, d.askedByName, d.completedBy, d.answeredBy, d.managerCommentBy, d.rejectedBy,
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
  };

  // Klasyfikacja: każdy item trafia do dokładnie JEDNEJ grupy (priorytet: comment > rejected > done > pending dla matters)
  const classify = (it) => {
    if (it.type === 'inquiry') return 'inquiries';
    const s = it.doc.status;
    if (it.doc.managerComment) return 'commented';
    if (s === 'rejected') return 'rejected';
    if (s === 'done') return 'done';
    return 'pending';
  };

  const filteredItems = items.filter(matchesSearch).filter((it) => {
    if (statusFilter === 'all') return true;
    return classify(it) === statusFilter;
  });
  const grouped = {
    inquiries: [],
    commented: [],
    done: [],
    rejected: [],
    pending: [],
  };
  filteredItems.forEach((it) => { grouped[classify(it)].push(it); });

  // Liczniki dla chipów (z items, nie filteredItems, żeby zawsze pokazywały sumy)
  const counts = { all: items.length, inquiries: 0, commented: 0, done: 0, rejected: 0, pending: 0 };
  items.filter(matchesSearch).forEach((it) => { counts[classify(it)]++; });

  const groupDefs = [
    { id: 'inquiries', label: 'Zadane pytania', emoji: '❓', color: '#3b82f6' },
    { id: 'commented', label: 'Z komentarzem managera', emoji: '💬', color: '#f59e0b' },
    { id: 'done', label: 'Zrealizowane zadania', emoji: '✅', color: '#22c55e' },
    { id: 'rejected', label: 'Odrzucone zadania', emoji: '🚫', color: '#ef4444' },
    { id: 'pending', label: 'Oczekujące zadania', emoji: '📋', color: '#94a3b8' },
  ];

  // Renderuj loading TYLKO przy pierwszym ładowaniu (nie przy silent refresh)
  if (loading && items.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Ładowanie…</div>;
  }
  if (!loading && !items.length) {
    return (
      <div style={{
        padding: 24, textAlign: 'center',
        background: 'var(--bg)', borderRadius: 12,
        border: '1px dashed var(--border)',
      }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Brak historii</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
          Twoje zgłoszenia + ostatnie rozwiązane sprawy i odpowiedzi z tej lokalizacji (synchroniczne z 🔔) pojawią się tutaj.
        </div>
        <button
          onClick={() => load(true)}
          data-testid="history-refresh-empty"
          style={{
            padding: '8px 16px', borderRadius: 8, border: '1px solid var(--gold)',
            background: 'transparent', color: 'var(--gold)', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >🔄 Odśwież</button>
      </div>
    );
  }

  return (
    <div data-testid="matters-history-list" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 Szukaj w treści, odpowiedzi, autorze…"
          data-testid="history-search"
          style={{
            flex: 1, padding: '10px 14px', borderRadius: 10,
            background: 'var(--bg)', border: '1px solid var(--border)',
            color: 'var(--text)', fontSize: 13,
            outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
          }}
        />
        <button
          onClick={() => load(true)}
          data-testid="history-refresh"
          disabled={refreshing}
          title="Odśwież historię"
          style={{
            width: 42, height: 42, borderRadius: 10,
            background: refreshing ? 'var(--bg-card)' : 'rgba(212,175,55,0.14)',
            border: '1px solid rgba(212,175,55,0.4)', color: 'var(--gold)',
            cursor: refreshing ? 'wait' : 'pointer', fontSize: 16,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'inherit', flexShrink: 0,
          }}
        >
          <span style={{ display: 'inline-block', transition: 'transform 0.5s', transform: refreshing ? 'rotate(360deg)' : 'none' }}>🔄</span>
        </button>
      </div>

      {/* Status filter chips */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: 'Wszystkie', emoji: '📑', color: '#d4af37' },
          { id: 'pending', label: 'Niewykonane', emoji: '📋', color: '#94a3b8' },
          { id: 'inquiries', label: 'Pytania', emoji: '❓', color: '#3b82f6' },
          { id: 'commented', label: 'Z komentarzem', emoji: '💬', color: '#f59e0b' },
          { id: 'done', label: 'Wykonane', emoji: '✅', color: '#22c55e' },
          { id: 'rejected', label: 'Odrzucone', emoji: '🚫', color: '#ef4444' },
        ].map((chip) => {
          const active = statusFilter === chip.id;
          const n = counts[chip.id] || 0;
          return (
            <button
              key={chip.id}
              onClick={() => setStatusFilter(chip.id)}
              data-testid={`history-filter-${chip.id}`}
              style={{
                padding: '6px 10px', borderRadius: 999, cursor: 'pointer',
                background: active ? `${chip.color}26` : 'var(--bg)',
                border: `1px solid ${active ? chip.color : 'var(--border)'}`,
                color: active ? chip.color : 'var(--text-muted)',
                fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
              }}
            >
              <span>{chip.emoji}</span>
              <span>{chip.label}</span>
              <span style={{
                fontSize: 10, padding: '1px 6px', borderRadius: 999,
                background: active ? `${chip.color}40` : 'var(--bg-card)',
                color: active ? chip.color : 'var(--text-muted)', fontWeight: 800,
              }}>{n}</span>
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1, overflowY: 'auto', paddingRight: 4, WebkitOverflowScrolling: 'touch' }}>
        {groupDefs.map((g) => {
          const list = grouped[g.id];
          if (!list.length) return null;
          const isCollapsed = !!collapsed[g.id];
          return (
            <section key={g.id} data-testid={`history-group-${g.id}`} style={{
              border: `1px solid ${g.color}40`,
              borderRadius: 12,
              background: `linear-gradient(180deg, ${g.color}0C 0%, transparent 100%)`,
              overflow: 'hidden',
            }}>
              <button
                type="button"
                onClick={() => toggleGroup(g.id)}
                data-testid={`history-group-toggle-${g.id}`}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px',
                  background: `${g.color}14`,
                  border: 'none', borderBottom: isCollapsed ? 'none' : `1px solid ${g.color}30`,
                  color: g.color, fontWeight: 800, fontSize: 13, cursor: 'pointer',
                  fontFamily: 'inherit', textAlign: 'left', letterSpacing: '0.02em',
                }}
              >
                <span style={{ fontSize: 16 }}>{g.emoji}</span>
                <span style={{ flex: 1, textTransform: 'uppercase' }}>{g.label}</span>
                <span style={{
                  fontSize: 11, padding: '3px 8px', borderRadius: 999,
                  background: `${g.color}26`, color: g.color,
                  border: `1px solid ${g.color}60`, fontWeight: 800,
                }}>{list.length}</span>
                <span style={{ fontSize: 12, opacity: 0.7, transition: 'transform 0.18s ease', display: 'inline-block', transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)' }}>▶</span>
              </button>

              {!isCollapsed && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10 }}>
                  {list.map((it) => {
                    const d = it.doc;
                    const isMatter = it.type === 'matter';
                    const title = isMatter ? d.title : (d.question || it.localEntry?.title);
                    const hasMore = (isMatter && d.description) || (!isMatter && d.answered && d.answer) ||
                                    (isMatter && d.managerComment) || (isMatter && d.status === 'done') ||
                                    (isMatter && d.status === 'rejected');
                    return (
                      <button
                        type="button"
                        key={`${it.type}-${d.id}`}
                        onClick={() => setSelected(it)}
                        data-testid={`history-item-${d.id}`}
                        style={{
                          padding: 12, background: 'var(--bg)', borderRadius: 10,
                          border: '1px solid var(--border)', cursor: 'pointer',
                          textAlign: 'left', fontFamily: 'inherit', width: '100%',
                          transition: 'border-color 0.15s, transform 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--gold)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 18, lineHeight: 1 }}>{isMatter ? '📋' : '❓'}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', wordBreak: 'break-word', lineHeight: 1.3 }}>
                              {title}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                              <LocBadge loc={d.location} />
                              <span>· {fmtDateTime(d.createdAt)}</span>
                              {it.source === 'feed' && (
                                <span style={{
                                  fontSize: 9, padding: '2px 6px', borderRadius: 999,
                                  background: 'rgba(245, 158, 11, 0.18)', color: '#f59e0b',
                                  border: '1px solid rgba(245, 158, 11, 0.45)', fontWeight: 800,
                                  letterSpacing: '0.05em', textTransform: 'uppercase',
                                }}>🔔 z dzwoneczka</span>
                              )}
                            </div>
                          </div>
                          <StatusBadge kind={it.type} status={d.status} answered={d.answered} />
                        </div>
                        {hasMore && (
                          <div style={{
                            marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border)',
                            fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4,
                          }}>
                            👁 Kliknij aby zobaczyć szczegóły
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}

        {filteredItems.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            Brak wyników{search ? ` dla zapytania „${search}"` : ` w wybranym filtrze`}.
          </div>
        )}
      </div>

      {/* Modal — pełny widok wybranego itemu */}
      {selected && (
        <PortalOverlay onClose={() => setSelected(null)}>
        <div
          onClick={() => setSelected(null)}
          data-testid="history-detail-modal-backdrop"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(6px)', zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="history-detail-modal"
            style={{
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 16, maxWidth: 560, width: '100%', maxHeight: '85vh',
              overflowY: 'auto', padding: 20, position: 'relative',
              boxShadow: '0 24px 60px -20px rgba(0,0,0,0.7)',
            }}
          >
            <button
              onClick={() => setSelected(null)}
              data-testid="history-detail-close"
              style={{
                position: 'absolute', top: 12, right: 12,
                width: 32, height: 32, borderRadius: 8,
                background: 'var(--bg)', border: '1px solid var(--border)',
                color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'inherit',
              }}
            >✕</button>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, paddingRight: 40 }}>
              <span style={{ fontSize: 28 }}>{selected.type === 'matter' ? '📋' : '❓'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)', lineHeight: 1.3, wordBreak: 'break-word' }}>
                  {selected.type === 'matter' ? selected.doc.title : (selected.doc.question || selected.localEntry?.title)}
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <LocBadge loc={selected.doc.location} />
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDateTime(selected.doc.createdAt)}</span>
                  <StatusBadge kind={selected.type} status={selected.doc.status} answered={selected.doc.answered} />
                </div>
              </div>
            </div>

            {selected.type === 'matter' && selected.doc.description && (
              <div style={{
                padding: 12, background: 'var(--bg)', borderRadius: 10,
                border: '1px solid var(--border)', marginBottom: 10,
                fontSize: 14, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>{selected.doc.description}</div>
            )}

            {selected.type === 'inquiry' && selected.doc.answered && selected.doc.answer && (
              <div style={{
                marginTop: 8, padding: 14,
                background: 'rgba(34, 197, 94, 0.10)',
                border: '1px solid rgba(34, 197, 94, 0.40)',
                borderRadius: 10,
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#22c55e', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  💬 Odpowiedź managera{selected.doc.answeredBy ? ` · ${selected.doc.answeredBy}` : ''}
                </div>
                <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {selected.doc.answer}
                </div>
                {selected.doc.answeredAt && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                    {fmtDateTime(selected.doc.answeredAt)}
                  </div>
                )}
              </div>
            )}

            {selected.type === 'matter' && selected.doc.status === 'done' && selected.doc.completedAt && (
              <div style={{
                marginTop: 10, padding: '10px 14px',
                background: 'rgba(34, 197, 94, 0.10)',
                border: '1px solid rgba(34, 197, 94, 0.40)',
                borderRadius: 10, fontSize: 13, color: '#22c55e', fontWeight: 700,
              }}>
                ✅ Wykonane {selected.doc.completedBy ? `przez ${selected.doc.completedBy}` : ''} · {fmtDateTime(selected.doc.completedAt)}
              </div>
            )}

            {selected.type === 'matter' && selected.doc.status === 'rejected' && (
              <div style={{
                marginTop: 10, padding: '10px 14px',
                background: 'rgba(239, 68, 68, 0.10)',
                border: '1px solid rgba(239, 68, 68, 0.40)',
                borderRadius: 10, fontSize: 13, color: '#ef4444', fontWeight: 700,
              }}>
                🚫 Odrzucone {selected.doc.rejectedBy ? `przez ${selected.doc.rejectedBy}` : ''}{selected.doc.rejectedAt ? ` · ${fmtDateTime(selected.doc.rejectedAt)}` : ''}
              </div>
            )}

            {selected.type === 'matter' && selected.doc.managerComment && (
              <div style={{
                marginTop: 10, padding: 14,
                background: 'rgba(245, 158, 11, 0.10)',
                border: '1px solid rgba(245, 158, 11, 0.40)',
                borderRadius: 10,
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#f59e0b', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  💬 Komentarz managera{selected.doc.managerCommentBy ? ` · ${selected.doc.managerCommentBy}` : ''}
                </div>
                <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {selected.doc.managerComment}
                </div>
              </div>
            )}
          </div>
        </div>
        </PortalOverlay>
      )}
    </div>
  );
};

export default MattersHistory;
